import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Form } from "@/components/ui/form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useSendContactRequest } from "../../api/contactForm.queries";
import { Spinner } from "@/components/ui/spinner";

const sectionFiveFormSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(50, "Name must be less than 50 characters"),
  email: z.string().email("Invalid email address"),
  company: z
    .string()
    .min(1, "Company is required")
    .max(50, "Company must be less than 50 characters"),
  message: z
    .string()
    .min(1, "Message is required")
    .max(500, "Message must be less than 500 characters"),
});

export default function SectionFive() {
  const form = useForm<z.infer<typeof sectionFiveFormSchema>>({
    resolver: zodResolver(sectionFiveFormSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      message: "",
    },
  });

  const { mutate: sendContactRequest, isPending } = useSendContactRequest();

  const onSubmit = (values: z.infer<typeof sectionFiveFormSchema>) => {
    sendContactRequest(values, {
      onSuccess: () => {
        form.reset();
      },
    });
  };

  return (
    <div className="relative isolate flex w-full justify-center">
      {/* Radial blur background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <div
          className="h-[900px] w-[900px] rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(64.83% 64.83% at 50% 50%, rgba(249, 115, 22, 0.2) 0%, rgba(255, 255, 255, 0) 100%)",
          }}
        />
      </div>

      {/* White form container */}
      <div className="relative w-full max-w-[720px] space-y-8 rounded-[28px] bg-white p-10 shadow-[0px_2px_20px_3px_#37322B1F]">
        <div className="space-y-1">
          <h1 className="text-[28px] leading-9 font-bold -tracking-wide text-neutral-950">
            Let's get in touch.
          </h1>
          <p className="text-[18px] leading-normal font-medium text-neutral-800">
            We'll show you how Metawhale works.
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full space-y-6"
          >
            <div className="grid grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-800">
                      Name <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder="Carl"
                        variant="filled"
                        className="h-[45px] w-full"
                        maxLength={50}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-bold text-neutral-800">
                      Email <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="email@example.com"
                        variant="filled"
                        className="h-[45px] w-full"
                        maxLength={50}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-bold text-neutral-800">
                    Company <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="text"
                      placeholder="Metawhale"
                      variant="filled"
                      className="h-[45px] w-full"
                      maxLength={50}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-bold text-neutral-800">
                    Message <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Enter your message here"
                      className="min-h-[129px] w-full border-0 bg-neutral-100 px-5 py-3 text-sm font-medium text-neutral-800 focus-visible:ring-0"
                      maxLength={500}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="h-[48px] w-full"
              disabled={isPending}
            >
              {isPending ? (
                <div className="flex h-full items-center justify-center text-white">
                  <Spinner size="sm" className="stroke-white!" />
                </div>
              ) : (
                "Send Message"
              )}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
